import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import AppData from 'app_data';
import './attention_table.scss';
import IntervalRunner from 'analytics-ui/IntervalRunner';
import { DefaultSpinner, NoData } from 'components';
import LocationScopeGate from 'hooks/useAdminGroup/LocationScopeGate';
import { useScopedApiFilters } from 'hooks/useAdminGroup/useScopedApiFilters';

class AttentionTable extends React.Component {
  static propTypes = {
    actions: PropTypes.object,
    state: PropTypes.object,
    /** Breadcrumb-scoped filters (scope_locations) merged in by the wrapper */
    scopedFilters: PropTypes.object,
  };

  state = {
    attentionTable: undefined,
  };

  // Latest fired request id. The client can't cancel requests, so responses
  // can land out of order; only the newest may commit.
  fetchSeq = 0;

  updateData = (state) => {
    const params = {
      // Real-time ranges stay open-ended so each refresh picks up new calls
      // (same convention as fetchChartData).
      time_range: state.realTime ? _.omit(state.timeRange, 'end') : state.timeRange,
      filters: this.props.scopedFilters,
    };

    const seq = ++this.fetchSeq;
    AppData.getProblemLocationData(params)
      .then((response) => {
        // Superseded by a newer request; drop this response.
        if (seq !== this.fetchSeq) {
          return;
        }
        this.setState({ attentionTable: response.data.tables[0] });
      });
  };

  componentDidMount() {
    this.updateData(this.props.state);
  };

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.scopedFilters, this.props.scopedFilters)) {
      this.updateData(this.props.state);
    }
  };

  // Real-time refresh for IntervalRunner. Must be a stable reference: the
  // runner restarts its timer whenever the action identity changes, and this
  // page re-renders more often than the interval fires.
  refresh = () => this.updateData(this.props.state);

  attentionChart() {
    if (!this.state.attentionTable) {
      return;
    };

    return (
      <IntervalRunner
        isActive={this.props.state.realTime}
        interval={AppData.user.refresh_rate}
        action={this.refresh}
      >
        <div className="attention_div">
          <div className="header_div">
            <p className="attention_header_div">Needs Attention</p>
            <div className="legend_div">
              <p className="good_legend legend"></p><p>Good</p>
              <p className="moderate_legend legend"></p><p>Moderate</p>
              <p className="poor_legend legend"></p><p>Poor</p>
            </div>
          </div>
          <div><p className="attention_location_header">Location</p></div>
          <div className="attention_table">
            <table>
              <thead>
                <tr className="attention_table_header">
                  {this.state.attentionTable.headers.map((name, index) => (
                    <th className="single_table_header" key={index}>{name.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {this.state.attentionTable.rows.map((item, index) => (
                  <tr className="attention_table_row" key={index}>
                    <td className="single_table_row">{item.location}</td>
                    <td className="single_table_row">{item.total_calls}</td>
                    {item.up_mos > 3.8 ? <td className="single_table_row good_value">{item.up_mos}</td> : item.up_mos < 3.5 ? <td className="single_table_row poor_value">{item.up_mos}</td> : <td className="single_table_row moderate_value">{item.up_mos}</td>}
                    {item.down_mos > 3.8 ? <td className="single_table_row good_value">{Math.round(item.down_mos * 100) / 100}</td> : item.down_mos < 3.5 ? <td className="single_table_row poor_value">{Math.round(item.down_mos * 100) / 100}</td> : <td className="single_table_row moderate_value">{Math.round(item.down_mos * 100) / 100}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </IntervalRunner>
    );
  };

  render() {
    return (
      <div>
        {this.attentionChart()}
      </div>
    );
  };
};

// "Needs Attention" fetches /quality/call-log/problem_locations directly
// (not through ReportPageChart), so the breadcrumb scope is attached here as
// scope_locations (enforced server-side by _resolve_locations). The gate
// holds the first mount until the scope resolves, so an unscoped request
// never fires.
const AttentionTableWithScope = (props) => {
  const controlsState = props.state || {};
  const { scope, apiFilters, hold, isEmptyIntersection } = useScopedApiFilters(
    controlsState.apiFilters,
    controlsState.filterOptions,
  );

  return (
    <LocationScopeGate scope={scope}>
      {hold ? (
        <DefaultSpinner />
      ) : isEmptyIntersection ? (
        <NoData />
      ) : (
        <AttentionTable {...props} scopedFilters={apiFilters} />
      )}
    </LocationScopeGate>
  );
};

AttentionTableWithScope.propTypes = {
  state: PropTypes.object,
};

export default AttentionTableWithScope;


